import { FieldConfig } from "../types/formTypes";

export interface BalanceData {
  standardHours?: number;
  currentYearBalance?: number;
  previousYearBalance?: number;
  currentYearEligibleOverride?: number;
  priorYearEligibleOverride?: number;
  currentYearEligibleRemaining?: number;
  priorYearEligibleRemaining?: number;
  currentYearOutstanding?: number;
  previousYearOutstanding?: number;
  currentYearActual?: number;
  previousYearActual?: number;
  currentYearReimbursable?: number;
  previousYearReimbursable?: number;
}

/**
 * Year type indicator for field pairs
 */
type YearType = "current" | "prior";

/**
 * Balance field group definition
 * Contains metadata to generate paired currentYear/previousYear fields
 */
interface BalanceFieldGroup {
  id: string;
  label: string;
  helperTextTemplate: (year: YearType) => string;
  disabled?: boolean;
  required?: boolean;
}

/**
 * Minimal balance field configuration data
 * Only contains unique values; structure generated by factory
 */
const balanceFieldGroups: BalanceFieldGroup[] = [
  {
    id: "balance",
    label: "Eligible amount",
    helperTextTemplate: (year) =>
      year === "current" ? "Current year eligible amount" : "Prior year eligible amount",
    disabled: true,
  },
  {
    id: "eligibleOverride",
    label: "Eligible amount override",
    helperTextTemplate: (year) =>
      year === "current"
        ? "Current year eligible amount overridden"
        : "Prior year eligible amount to be paid",
    disabled: false,
  },
  {
    id: "outstanding",
    label: "Outstanding estimated fees",
    helperTextTemplate: (year) =>
      year === "current"
        ? "Current year outstanding estimated fees"
        : "Prior year outstanding estimated fees",
    disabled: true,
  },
  {
    id: "actual",
    label: "Actual fees",
    helperTextTemplate: (year) =>
      year === "current" ? "Current year actual fees" : "Prior year actual fees",
    disabled: true,
  },
  {
    id: "reimbursable",
    label: "Reimbursement fees",
    helperTextTemplate: (year) =>
      year === "current" ? "Current year reimbursable fees" : "Prior year reimbursable fees",
    disabled: true,
  },
  {
    id: "eligibleRemaining",
    label: "Estimated remaining eligible amount",
    helperTextTemplate: (year) => (year === "current" ? "Current year" : "Prior year"),
    disabled: true,
    required: true,
  },
];

/**
 * Mapping of field ID to currentYear/previousYear field names
 */
const fieldNameMap: Record<string, { current: string; prior: string }> = {
  balance: { current: "currentYearBalance", prior: "previousYearBalance" },
  eligibleOverride: {
    current: "currentYearEligibleOverride",
    prior: "priorYearEligibleOverride",
  },
  outstanding: {
    current: "currentYearOutstanding",
    prior: "previousYearOutstanding",
  },
  actual: { current: "currentYearActual", prior: "previousYearActual" },
  reimbursable: {
    current: "currentYearReimbursable",
    prior: "previousYearReimbursable",
  },
  eligibleRemaining: {
    current: "currentYearEligibleRemaining",
    prior: "priorYearEligibleRemaining",
  },
};

/**
 * Factory function to generate a year-pair of FieldConfig entries
 * Creates both currentYear and previousYear variants from a single definition
 */
const createYearPairFields = (
  group: BalanceFieldGroup
): [FieldConfig, FieldConfig] => {
  const names = fieldNameMap[group.id];

  const createField = (year: YearType): FieldConfig => ({
    name: year === "current" ? names.current : names.prior,
    label: group.label,
    type: "text",
    required: group.required ?? false,
    helperText: group.helperTextTemplate(year),
    placeholder: "i.e $725",
    disabled: group.disabled ?? true,
  });

  return [createField("current"), createField("prior")];
};

/**
 * Balance fields configuration
 * Generated from balanceFieldGroups to eliminate structural duplication
 */
export const balanceFields: FieldConfig[] = [
  // Standard hours - standalone field
  {
    name: "standardHours",
    label: "Standard hours",
    type: "number",
    required: false,
    helperText: "Current year standard hours",
    placeholder: "i.e. 108",
    disabled: true,
  },
  // Year-pair fields generated from groups
  ...balanceFieldGroups.flatMap((group) => createYearPairFields(group)),
];

export default balanceFields;